import { useQuery } from "@tanstack/react-query";

import { instance } from "@/lib/axios/instance";
import { useAuth } from "@/lib/hooks/useAuth";

type ModuleIntegrationsResponse = {
  integrations: string[];
};

type UseModuleStatusOptions = {
  /** Optional client override for SuperAdmin viewing a specific client. Other roles inherit scope from JWT. */
  clientId?: string | null;
};

export const moduleStatusQueryKey = (moduleName: string, clientId: string | null, tenantId: string | null) =>
  [moduleName, "moduleStatus", { clientId, tenantId }] as const;

/**
 * Generic check for whether a module is active for the user's current scope.
 * Queries GET /api/v2/Modules/{moduleName}/integrations and reports `enabled: true`
 * when at least one integration has the module active.
 *
 * Scope is resolved server-side: HttpContextTenantProvider reads tenantId/clientId
 * from the JWT for Admin/Manager/Spectator, and the controller prefers the provider
 * over query params. SuperAdmin has no scope in the JWT, so the auth tenantId
 * (set via the SuperAdmin tenant selector and stored in localStorage by AuthProvider)
 * and an optional clientId override are forwarded as query params.
 *
 * Gated on `accessToken` so the request only fires for authenticated users.
 * SuperAdmin without a tenant selection will receive a 400 from the backend —
 * React Query treats that as an error, so consumers reading `data?.enabled` see
 * `undefined` and treat the module as disabled.
 */
export function useModuleStatus(moduleName: string, options?: UseModuleStatusOptions) {
  const { accessToken, tenantId } = useAuth();
  const clientId = options?.clientId ?? null;
  return useQuery({
    queryKey: moduleStatusQueryKey(moduleName, clientId, tenantId),
    enabled: !!accessToken,
    queryFn: () => {
      const params: Record<string, string> = {};
      if (clientId) params.clientId = clientId;
      if (tenantId) params.tenantId = tenantId;
      return instance
        .get<ModuleIntegrationsResponse>(`/api/v2/Modules/${encodeURIComponent(moduleName)}/integrations`, {
          params: Object.keys(params).length ? params : undefined,
        })
        .then((res) => ({ enabled: res.data.integrations.length > 0 }));
    },
    staleTime: 5 * 60 * 1000,
  });
}
